import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useApp } from "@/context/AppContext";
import API from "@/lib/api";
import { distanceLabel } from "@/lib/format";
import { ArrowLeft, Star, Phone, Globe, Navigation, FileText, BadgeCheck, Clock, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function BusinessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getCategory, catName } = useApp();
  const [b, setB] = useState(null);
  const [fiscal, setFiscal] = useState("");
  const [contact, setContact] = useState("");
  const [claimOpen, setClaimOpen] = useState(false);

  const load = () => API.get(`/businesses/${id}`).then((r) => setB(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);
  if (!b) return <Layout><p className="text-[#4A4E68]">Caricamento...</p></Layout>;

  const cat = getCategory(b.category);
  const c = b.public_contacts || {};
  const askQuote = () => navigate(`/publish/${b.category}?business=${b.id}&businessName=${encodeURIComponent(b.trade_name)}`);
  const claim = async () => {
    try {
      const res = await API.post(`/businesses/${id}/claim`, { fiscal_number: fiscal, contact });
      toast.success(res.data.message || "Scheda rivendicata");
      setClaimOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <Layout>
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-[#4A4E68] mb-3 text-sm"><ArrowLeft size={16} /> Indietro</button>
      <div className="bg-white rounded-[18px] border border-[#DADFEA] p-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h1 className="font-serif text-2xl font-bold text-[#0F1230]">{b.trade_name}</h1>
            <p className="text-sm text-[#4A4E68]">{cat ? catName(cat) : b.category}{b.city ? ` · ${b.city}` : ""}{b.distance_km != null ? ` · ${distanceLabel(b.distance_km)}` : ""}</p>
          </div>
          {b.avg_rating != null && <span className="text-sm text-[#4A4E68] inline-flex items-center gap-0.5 shrink-0"><Star size={15} className="fill-[#FFB800] text-[#FFB800]" /> {b.avg_rating} ({b.review_count})</span>}
        </div>

        <div className="mt-2 flex flex-wrap gap-1.5">
          {b.admin_verified && <span className="inline-flex items-center gap-1 rounded-full bg-[#E8F5E9] text-[#2E7D32] px-2 py-0.5 text-xs font-medium"><BadgeCheck size={13} /> Attività verificata</span>}
          {b.registry?.verified && <span className="inline-flex items-center gap-1 rounded-full bg-[#E8F5E9] text-[#2E7D32] px-2 py-0.5 text-xs font-medium"><ShieldCheck size={13} /> Iscrizione verificata</span>}
          {!b.claimed && <span className="inline-flex items-center gap-1 rounded-full bg-[#EDEDF4] text-[#4A4E68] px-2 py-0.5 text-xs">Scheda non rivendicata</span>}
        </div>

        {b.description && <p className="text-[#2A2E48] mt-4">{b.description}</p>}
        {b.registry?.order && <p className="text-sm text-[#4A4E68] mt-2">{b.registry.order} · n. {b.registry.number}</p>}
        {b.hours && <p className="text-sm text-[#4A4E68] mt-1 inline-flex items-center gap-1"><Clock size={13} /> {b.hours}</p>}

        <div className="flex flex-wrap gap-2 mt-4">
          {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 text-sm border border-[#DADFEA] rounded-full px-4 h-10"><Phone size={15} /> Chiama</a>}
          <a href={`https://www.openstreetmap.org/search?query=${encodeURIComponent((b.trade_name || "") + " " + (b.city || ""))}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm border border-[#DADFEA] rounded-full px-4 h-10"><Navigation size={15} /> Indicazioni</a>
          {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm border border-[#DADFEA] rounded-full px-4 h-10"><Globe size={15} /> Sito</a>}
        </div>

        {!b.claimed && (
          <div className="mt-4 bg-[#FFF1E6] border border-[#FFD8BF] rounded-xl p-3" data-testid="guided-claim-banner">
            <p className="text-sm text-[#B8420A] font-medium">Questa scheda è stata cercata {b.interest_count || 0} volte su FYDATE.</p>
            <p className="text-xs text-[#B8420A]">Sei il titolare? Rivendicala per gestire i contatti e ricevere le richieste.</p>
          </div>
        )}

        <Button data-testid="detail-ask-quote" onClick={askQuote} className="w-full h-12 bg-[#FFD23F] hover:bg-[#F2C200] text-[#0F1230] rounded-full mt-4"><FileText size={16} className="mr-1" /> Chiedi un preventivo</Button>

        {!b.claimed && (
          <Dialog open={claimOpen} onOpenChange={setClaimOpen}>
            <DialogTrigger asChild>
              <Button data-testid="claim-button" variant="outline" className="w-full h-11 rounded-full border-[#1F4FE0] text-[#141A3A] mt-2">Sei il titolare? Rivendica questa scheda</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-serif">Rivendica la scheda</DialogTitle>
                <DialogDescription>Verifica con la partita IVA e un contatto aziendale.</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <Input data-testid="claim-fiscal" placeholder="Partita IVA" value={fiscal} onChange={(e) => setFiscal(e.target.value)} />
                <Input data-testid="claim-contact" placeholder="Email o telefono aziendale" value={contact} onChange={(e) => setContact(e.target.value)} />
              </div>
              <DialogFooter>
                <Button data-testid="claim-submit" onClick={claim} disabled={!fiscal} className="bg-[#1F4FE0] text-white rounded-full">Invia richiesta</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {b.reviews?.length > 0 && (
        <div className="mt-5">
          <h2 className="font-serif text-xl font-semibold text-[#0F1230] mb-3">Recensioni</h2>
          <div className="space-y-2">
            {b.reviews.map((rv) => (
              <div key={rv.id} className="bg-white rounded-[18px] border border-[#DADFEA] p-4">
                <div className="flex items-center gap-0.5 text-[#FFB800] mb-1">{Array.from({ length: rv.rating }).map((_, i) => <Star key={i} size={14} className="fill-[#FFB800]" />)}</div>
                <p className="text-sm text-[#2A2E48]">{rv.comment}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
}
